import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import LabelInput from '../../../shared/LabelInput';

import {
    useDeleteDiscount,
    usePackDiscount,
    useSaveDiscount,
} from '../../../hooks/discounts';

import { toHTMLDate } from '../../../utils/dates.ts';
import { handleError } from '../../../utils/utils';

import styles from './PackDiscount.module.scss';
import validateDiscount from './validate';

type PackDiscountProps = {
    sku: string;
};

type DiscountState = {
    discountPrice: string;
    startDate: string;
    endDate: string;
};

const emptyDiscount: DiscountState = {
    discountPrice: '',
    startDate: '',
    endDate: '',
};

export enum PossibleDiscountFields {
    DiscountPrice = 'discountPrice',
    StartDate = 'startDate',
    EndDate = 'endDate',
}

const toDateInputValue = (date: string) => date.slice(0, 10);

const getInitialDiscountState = (
    discount?: {
        discountPrice: number;
        startDate: string;
        endDate: string;
    } | null,
): DiscountState => {
    if (!discount) {
        return emptyDiscount;
    }

    return {
        discountPrice: String(discount.discountPrice),
        startDate: toDateInputValue(discount.startDate),
        endDate: toDateInputValue(discount.endDate),
    };
};

export function PackDiscount({ sku }: PackDiscountProps) {
    const [draftDiscount, setDraftDiscount] = useState<DiscountState | null>(
        null,
    );
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!statusMessage) {
            return;
        }

        setTimeout(() => {
            setStatusMessage(null);
        }, 5000);
    }, [statusMessage]);

    const { data: fetchedDiscount, isLoading } = usePackDiscount(sku);
    const saveDiscount = useSaveDiscount();
    const deleteDiscount = useDeleteDiscount();

    const initialDiscount = useMemo(
        () => getInitialDiscountState(fetchedDiscount),
        [fetchedDiscount],
    );

    useEffect(() => {
        setDraftDiscount(initialDiscount);
    }, [initialDiscount]);

    const discount = draftDiscount ?? initialDiscount;

    const hasUnsavedChanges = useMemo(() => {
        return (
            discount.discountPrice !== initialDiscount.discountPrice ||
            discount.startDate !== initialDiscount.startDate ||
            discount.endDate !== initialDiscount.endDate
        );
    }, [discount, initialDiscount]);

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name as PossibleDiscountFields;

        setStatusMessage(null);
        setErrorMessage(null);
        setDraftDiscount((prev) => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                [name]: event.target.value,
            };
        });
    };

    const save = async () => {
        const validationResult = validateDiscount(discount);
        if (validationResult.error) {
            setErrorMessage(
                validationResult.error.details
                    .map((e) => e.message)
                    .join('\n') ?? 'Validation failed.',
            );
            return;
        }

        const payload = {
            discountPrice: Number(discount.discountPrice),
            startDate: discount.startDate,
            endDate: discount.endDate,
        };

        try {
            await saveDiscount.mutateAsync({ sku, payload });

            setDraftDiscount(null);
            setStatusMessage('Discount schedule saved.');
        } catch (error) {
            setErrorMessage(
                handleError('Failed to save discount schedule', error, false),
            );
        }
    };

    const remove = async () => {
        if (!fetchedDiscount) {
            return;
        }

        try {
            await deleteDiscount.mutateAsync({ id: fetchedDiscount.id, sku });

            setDraftDiscount(null);
            setStatusMessage('Discount schedule deleted.');
        } catch (error) {
            setErrorMessage(
                handleError('Failed to delete discount schedule', error, false),
            );
        }
    };

    const isSaving = saveDiscount.isPending;
    const isDeleting = deleteDiscount.isPending;

    const canSave = useMemo(() => {
        return (
            Boolean(sku) &&
            Boolean(discount.discountPrice) &&
            Boolean(discount.startDate) &&
            Boolean(discount.endDate)
        );
    }, [discount.discountPrice, discount.endDate, discount.startDate, sku]);

    if (!sku) {
        return <div>Set SKU first to configure a discount period.</div>;
    }

    if (isLoading) {
        return <div>Loading discount schedule...</div>;
    }

    return (
        <div className={styles.discountBlock}>
            <div className={styles.inputsRow}>
                <LabelInput
                    className="prices"
                    size="narrow"
                    type="number"
                    inputMode="decimal"
                    name={PossibleDiscountFields.DiscountPrice}
                    label="Discount price"
                    value={discount.discountPrice}
                    onChange={onInputChange}
                />

                <LabelInput
                    size="normal"
                    type="datetime-local"
                    name={PossibleDiscountFields.StartDate}
                    label="Starts"
                    value={toHTMLDate(discount.startDate)}
                    onChange={onInputChange}
                />

                <LabelInput
                    size="normal"
                    type="datetime-local"
                    name={PossibleDiscountFields.EndDate}
                    label="Ends"
                    value={toHTMLDate(discount.endDate)}
                    onChange={onInputChange}
                />
            </div>

            <div className={styles.actions}>
                <button
                    className="positive"
                    onClick={save}
                    disabled={
                        !hasUnsavedChanges || !canSave || isSaving || isDeleting
                    }
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
                {fetchedDiscount && (
                    <button
                        className="danger"
                        onClick={remove}
                        disabled={isSaving || isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                )}
            </div>

            {errorMessage && <div className={styles.error}>{errorMessage}</div>}
            {statusMessage && (
                <div className={styles.success}>{statusMessage}</div>
            )}
        </div>
    );
}
